import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";

/**
 * Shared empno -> photo-URL map (TECH-PHOTOS). One cached query feeds every
 * avatar on the page; only techs WITH a photo appear here, so everyone else
 * keeps their colored initials (no broken-image 404s). Photos are imported
 * from ESC's Employee.Photo by import-employee-photos.ps1 into the public
 * `tech-photos` bucket.
 */
export function useTechPhotos() {
  return useQuery({
    queryKey: ["tech_photos"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data } = await supabase.from("tech_photo").select("empno, url");
      const map: Record<string, string> = {};
      for (const r of (data ?? []) as { empno: string; url: string }[]) {
        if (r.empno && r.url) map[r.empno] = r.url;
      }
      return map;
    },
  });
}

interface Props {
  empno: string;
  name: string;
  /**
   * When the tech has a profile photo uploaded, pass the URL here.
   * Falls back to colored initials when null/undefined.
   * Transparent PNGs work great because we render a colored background.
   */
  imageUrl?: string | null;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses: Record<NonNullable<Props["size"]>, string> = {
  xs: "h-6 w-6 text-[11px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-base",
  xl: "h-20 w-20 text-xl",
};

// Curated set of brand-friendly gradient backgrounds. Each tech gets a
// deterministic one based on their empno so the same person always shows
// the same color across sessions.
const gradientPalette = [
  "from-pink-500 to-rose-700",
  "from-fuchsia-500 to-purple-700",
  "from-purple-500 to-violet-700",
  "from-indigo-500 to-blue-700",
  "from-blue-500 to-cyan-700",
  "from-cyan-500 to-teal-700",
  "from-teal-500 to-emerald-700",
  "from-emerald-500 to-green-700",
  "from-lime-500 to-green-700",
  "from-amber-500 to-orange-700",
  "from-orange-500 to-red-700",
  "from-red-500 to-rose-700",
] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function pickGradient(empno: string): string {
  return gradientPalette[hashString(empno) % gradientPalette.length];
}

function getInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

/**
 * Tech avatar - shows uploaded photo (transparent PNG over colored bg)
 * when available, falls back to colored initials.
 *
 * Use everywhere we display a tech reference - Board column headers,
 * Customer 360 dispatch history, modals, hover cards, etc.
 */
export function TechAvatar({
  empno,
  name,
  imageUrl,
  size = "sm",
  className,
}: Props) {
  const gradient = pickGradient(empno);
  const initials = getInitials(name);
  // Use an explicitly-passed photo if given, else the tech's imported ESC headshot.
  const photos = useTechPhotos().data;
  const resolved = imageUrl ?? photos?.[empno] ?? null;

  return (
    <div
      className={cn(
        "flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br font-semibold text-white shadow-sm",
        gradient,
        sizeClasses[size],
        className,
      )}
      title={name}
    >
      {resolved ? (
        <img
          src={resolved}
          alt={name}
          className="h-full w-full object-cover"
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}
